import React, { ReactElement } from 'react';
import ScreenService from '../../services/screen-service';
import DateService from '../../services/date-service';
import useMobileDevice from '../../hooks/use-mobile-device';
import { HTML_TAG_NAME_ANCHOR, HTML_TAG_NAME_SVG, HTML_TAG_NAME_SVG_PATH } from '../../constants/system/html-tag-names';
import POINTER_KEY_PRIMARY from '../../constants/system/pointer-keys';

const LongClickItem: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const LONG_CLICK_DELAY: number = 1200;
  const LONG_CLICK_DELAY_MOBILE: number = 4000;

  const SKIPPED_HTML_TAG_LIST: string[] = [
    HTML_TAG_NAME_ANCHOR,
    HTML_TAG_NAME_SVG,
    HTML_TAG_NAME_SVG_PATH,
  ];

  const {
    className,
    onClick,
    onLongClick,
    component,
    componentProps,
    children,
  } = props;

  const isMobileDevice: boolean = useMobileDevice();

  const InnerComponent: ReactComponent = component;
  const longClickDelay: number = isMobileDevice ? LONG_CLICK_DELAY_MOBILE : LONG_CLICK_DELAY;

  let timeoutId: TimeoutId | null = null;
  let clickTime: number | null = null;
  let isInterrupted: boolean = false;

  const resetTimer: Function = (): void => {
    if (timeoutId) {
      clearTimeout(timeoutId);

      timeoutId = null;
    }
  };

  const onPointerDown: PointerEventHandlerFunction = async (event: React.PointerEvent): Promise<void> => {
    if (event.button === POINTER_KEY_PRIMARY && (event.target as HTMLElement).tagName
      && !SKIPPED_HTML_TAG_LIST.includes((event.target as HTMLElement).tagName.toLowerCase())) {
      event.persist();

      clickTime = DateService.getCurrentDateTimeStamp();
      isInterrupted = false;

      resetTimer();

      timeoutId = setTimeout(((): void => {
        const withNoSelection: boolean = ScreenService.checkNoSelection();

        if (onLongClick && !isInterrupted && withNoSelection) {
          onLongClick(event);
        }

        resetTimer();

        isInterrupted = false;
      }), longClickDelay);
    }
  };

  const onPointerUp: PointerEventHandlerFunction = (event: React.PointerEvent): void => {
    if (event.button === POINTER_KEY_PRIMARY) {
      if (clickTime && clickTime + longClickDelay >= DateService.getCurrentDateTimeStamp()) {
        isInterrupted = true;
      }

      resetTimer();
    }
  };

  const onTouchEnd: PointerEventHandlerFunction = (): void => {
    if (isMobileDevice) {
      resetTimer();
    }
  };

  const click: MouseEventHandlerFunction = (event: React.MouseEvent<Element, MouseEvent>): void => {
    if (clickTime) {
      clickTime = null;
    }

    if (onClick) {
      onClick(event);
    }
  };

  return (
    <InnerComponent
      className={className}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onTouchEnd={onTouchEnd}
      onClick={click}
      {...componentProps}
    >
      {children}
    </InnerComponent>
  );
});

export default LongClickItem;
